// src/components/marketing/ApprovalQueue.tsx
// Cola de aprobación — drafts (status='draft') generados por el auto-poster
// que el usuario revisa antes de que salgan agendados. Filtra por el
// negocio activo en BusinessContext. El endpoint de aprobación real
// viene en la Fase 2 commit B; por ahora se usa updatePost(status=scheduled)
// + scheduled_at como fallback mientras no existe /drafts/{id}/approve.

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, Building2, Calendar, CheckCheck, CheckCircle, ClipboardList,
  Loader2, RefreshCw, Sparkles, Trash2, X,
} from 'lucide-react';

import { useActiveBusiness } from '@/context/BusinessContext';
import { BusinessSelector } from '@/components/businesses/BusinessSelector';
import { ApiError } from '@/lib/api_social_publish';
import {
  approveAllDrafts, approveDraft, listBusinessDrafts, rejectDraft,
  triggerGenerateContent, type BusinessDraft,
} from '@/lib/api_businesses';

/** Returns an ISO-local string (no TZ) for <input type="datetime-local">. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse a datetime-local value back into a Date. The browser gives a
 *  local-time string; we convert to ISO UTC for the server. */
function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props { addNotification?: Notifier; }

export function ApprovalQueue({ addNotification }: Props) {
  const { activeId } = useActiveBusiness();
  const [rows, setRows] = useState<BusinessDraft[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [modal, setModal] = useState<
    | { kind: 'single'; draft: BusinessDraft }
    | { kind: 'all'; count: number }
    | null
  >(null);
  const [modalTime, setModalTime] = useState<string>('');
  const [modalStagger, setModalStagger] = useState<number>(15);
  const [modalBusy, setModalBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const data = await listBusinessDrafts(activeId, 50);
      setRows(data);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudieron cargar drafts', message: msg });
    } finally { setLoading(false); }
  }, [activeId, addNotification]);

  useEffect(() => { load(); }, [load]);

  const openApproveModal = (p: BusinessDraft) => {
    const d = new Date(Date.now() + 60 * 60 * 1000); // +1h default
    setModalTime(toLocalInput(d));
    setModal({ kind: 'single', draft: p });
  };

  const openApproveAllModal = () => {
    if (!rows || rows.length === 0) return;
    const d = new Date(Date.now() + 60 * 60 * 1000);
    setModalTime(toLocalInput(d));
    setModalStagger(15);
    setModal({ kind: 'all', count: rows.length });
  };

  const confirmApprove = async () => {
    if (!activeId || !modal) return;
    setModalBusy(true);
    try {
      const iso = fromLocalInput(modalTime);
      if (modal.kind === 'single') {
        const res = await approveDraft(activeId, modal.draft.id, { scheduled_at: iso });
        const when = new Date(res.scheduled_at);
        addNotification?.({
          type: 'success', title: 'Draft aprobado',
          message: `Programado para ${when.toLocaleString('es-MX')}`,
        });
      } else {
        const res = await approveAllDrafts(activeId, {
          scheduled_at: iso, stagger_minutes: modalStagger,
        });
        addNotification?.({
          type: 'success', title: `${res.approved_count} drafts aprobados`,
          message: modalStagger
            ? `Separados ${modalStagger} min cada uno a partir de ${new Date(res.base_scheduled_at).toLocaleString('es-MX')}`
            : `Todos al ${new Date(res.base_scheduled_at).toLocaleString('es-MX')}`,
        });
      }
      setModal(null);
      await load();
    } catch (e: any) {
      addNotification?.({
        type: 'error', title: 'No se pudo aprobar',
        message: e?.message || 'Error',
      });
    } finally {
      setModalBusy(false);
    }
  };

  const reject = async (p: BusinessDraft) => {
    if (!activeId) return;
    if (!confirm('¿Eliminar este draft? No se podrá recuperar.')) return;
    setBusyId(p.id);
    try {
      await rejectDraft(activeId, p.id);
      addNotification?.({ type: 'success', title: 'Draft eliminado' });
      await load();
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'No se pudo eliminar', message: e?.message || 'Error' });
    } finally { setBusyId(null); }
  };

  const generateNow = async () => {
    if (!activeId) return;
    const instructions = prompt('Instrucciones especiales para este post (opcional):') || '';
    setGenerating(true);
    try {
      await triggerGenerateContent(activeId, { custom_instructions: instructions, force: true });
      addNotification?.({
        type: 'info',
        title: 'Generación encolada',
        message: 'Un draft puede tardar 1-3 min (LLM + imagen con gpt-image-2). Refresco cada 15s hasta que llegue.',
      });
      // Poll every 15s up to 3 minutes — gpt-image-2 is slow, the
      // worker needs time. The first successful draft bumps the list
      // and shows the generator spinner state off.
      const before = rows?.length ?? 0;
      let ticks = 0;
      const iv = setInterval(async () => {
        ticks += 1;
        try {
          const latest = await listBusinessDrafts(activeId, 50);
          if (latest.length > before) {
            clearInterval(iv);
            setRows(latest);
            addNotification?.({ type: 'success', title: 'Draft listo', message: 'Revísalo abajo y aprueba o rechaza.' });
          } else if (ticks >= 12) {
            clearInterval(iv);
            addNotification?.({ type: 'info', title: 'Sigue generándose', message: 'Tarda más de lo normal. Puedes refrescar manual después.' });
          }
        } catch { /* transient, keep trying */ }
      }, 15000);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'No se pudo disparar', message: e?.message || 'Error' });
    } finally { setGenerating(false); }
  };

  return (
    <div className="p-4 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <ClipboardList size={18} style={{ color: 'var(--acc1)' }} /> Cola de aprobación
          </h2>
          <p className="text-white/40 text-xs mt-0.5">
            Drafts generados por auto-posting que esperan tu revisión antes de agendarse.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BusinessSelector compact />
          <button
            onClick={generateNow}
            disabled={!activeId || generating}
            className="px-3 py-1.5 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-1 text-xs"
          >
            {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generar ahora
          </button>
          {rows && rows.length > 0 && (
            <button
              onClick={openApproveAllModal}
              className="px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-300 flex items-center gap-1 text-xs"
              title="Aprobar todos los drafts con un horario común"
            >
              <CheckCheck size={12} /> Aprobar todos ({rows.length})
            </button>
          )}
          <button onClick={load} disabled={loading} className="o-btn-ghost p-1.5 rounded-md" title="Refrescar">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </header>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !modalBusy && setModal(null)}>
          <div
            className="w-full max-w-md rounded-xl border border-white/10 bg-[#1e1f2b] p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                  <Calendar size={18} style={{ color: 'var(--acc1)' }} />
                  {modal.kind === 'single' ? 'Aprobar draft' : `Aprobar ${modal.count} drafts`}
                </h3>
                <p className="text-white/50 text-xs mt-0.5">
                  {modal.kind === 'single'
                    ? 'Elige cuándo debe publicarse. Default: dentro de 1 hora.'
                    : 'Todos quedan programados a partir del mismo horario, separados N minutos cada uno si defines stagger.'}
                </p>
              </div>
              <button onClick={() => !modalBusy && setModal(null)} className="text-white/40 hover:text-white">
                <X size={16} />
              </button>
            </div>

            <label className="block">
              <span className="text-[11px] text-white/50 uppercase tracking-wider">Programar para</span>
              <input
                type="datetime-local"
                className="input-base mt-1"
                value={modalTime}
                onChange={(e) => setModalTime(e.target.value)}
              />
            </label>

            {modal.kind === 'all' && (
              <label className="block">
                <span className="text-[11px] text-white/50 uppercase tracking-wider">Espaciar cada N minutos (opcional)</span>
                <input
                  type="number"
                  min={0}
                  max={120}
                  className="input-base mt-1"
                  value={modalStagger}
                  onChange={(e) => setModalStagger(Number(e.target.value))}
                />
                <p className="text-[10px] text-white/40 mt-1">
                  0 = todos al mismo tiempo. 15 = primer post a la hora elegida, segundo a +15 min, etc.
                </p>
              </label>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setModal(null)}
                disabled={modalBusy}
                className="px-3 py-2 rounded-lg text-white/60 hover:text-white/80 text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={confirmApprove}
                disabled={modalBusy || !modalTime}
                className="px-4 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-300 flex items-center gap-2 text-sm"
              >
                {modalBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                {modal.kind === 'single' ? 'Confirmar' : `Aprobar ${modal.count}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {!activeId ? (
        <div className="o-card flex items-center gap-2 text-amber-300">
          <AlertTriangle size={14} /> Selecciona un negocio para ver sus drafts.
        </div>
      ) : loading && !rows ? (
        <div className="py-12 flex justify-center text-white/40 text-sm"><Loader2 size={16} className="animate-spin" /></div>
      ) : !rows || rows.length === 0 ? (
        <div className="o-card text-center py-10 text-white/50">
          <Building2 size={28} className="mx-auto mb-2 text-white/20" />
          <p>No hay drafts pendientes.</p>
          <p className="text-xs mt-1">Cuando el auto-poster genere contenido, aparecerá aquí esperando tu aprobación.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => {
            const firstImg = (p.media || []).find((m) => m.kind === 'image');
            const firstVid = (p.media || []).find((m) => m.kind === 'video');
            return (
              <div key={p.id} className="o-card flex items-start gap-3">
                {firstImg ? (
                  <a
                    href={firstImg.media_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-32 h-32 shrink-0 rounded overflow-hidden bg-black/20"
                    title="Abrir tamaño completo"
                  >
                    <img
                      src={firstImg.media_url}
                      alt={firstImg.alt_text || 'preview'}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ) : firstVid ? (
                  <video
                    src={firstVid.media_url}
                    poster={firstVid.thumbnail_url || undefined}
                    controls
                    className="w-32 h-32 shrink-0 rounded bg-black/20 object-cover"
                  />
                ) : null}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/40">
                    <span>{p.platform}</span>
                    <span>·</span>
                    <span>{p.post_type}</span>
                    {p.ai_generated && <span className="text-emerald-400">· AI</span>}
                    {(p.media?.length ?? 0) > 1 && (
                      <span className="text-white/50">· {p.media.length} media</span>
                    )}
                  </div>
                  <div className="text-white text-sm mt-1 whitespace-pre-wrap break-words">
                    {p.caption || <em className="text-white/30">(sin caption)</em>}
                  </div>
                  {p.hashtags?.length ? (
                    <div className="text-[11px] text-white/40 mt-1">
                      {p.hashtags.map((h) => `#${h}`).join(' ')}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={() => openApproveModal(p)}
                    disabled={busyId === p.id}
                    className="px-3 py-1.5 rounded-md bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-1"
                  >
                    {busyId === p.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle size={11} />} Aprobar
                  </button>
                  <button
                    onClick={() => reject(p)}
                    disabled={busyId === p.id}
                    className="px-3 py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs flex items-center gap-1"
                  >
                    <Trash2 size={11} /> Rechazar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ApprovalQueue;
